/**
 * Copyright (c) 2022–2026 Podstack.ai
 * All rights reserved.
 * Proprietary and Confidential.
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 * This file is part of the Podstack Platform.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CpuChipIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import CreateSSHKeyModal from '../components/pods/CreateSSHKeyModal';
import {
  pricingService,
  sshKeyService,
} from '../services/api';
import { vmApi } from '../services/vmApi';
import toast from 'react-hot-toast';
import { VMCreateRequest, VMCostEstimate, OSImage, VMBillingPeriod, VMRegionInfo, VMUseCase } from '../types/vm';
import { GPUPricing, GPUAvailability, SSHKey } from '../types';
import InsufficientBalanceModal from '../components/wallet/InsufficientBalanceModal';
import { useWallet } from '../hooks/useWallet';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { SHOW_VM_COMING_SOON, VM_SHOW_GPU_CONFIGURATION } from '../config/vmPortalFeatures';

const billingPeriods: { value: VMBillingPeriod; label: string }[] = [
  { value: 'month', label: 'Per Month' },
  { value: 'quarter', label: 'Per Quarter' },
  { value: 'annual', label: 'Per Year' },
];

type Step = 'project' | 'region' | 'useCase' | 'basic' | 'resources' | 'os' | 'ssh' | 'review';

// Static region data
const regions: VMRegionInfo[] = [
  { id: 'india', name: 'india', displayName: 'India', location: 'Mumbai', flag: '🇮🇳', isAvailable: true, description: 'Low latency for Indian users' },
  { id: 'us-east', name: 'us-east', displayName: 'US East', location: 'Virginia', flag: '🇺🇸', isAvailable: false, description: 'Coming soon' },
  { id: 'us-west', name: 'us-west', displayName: 'US West', location: 'California', flag: '🇺🇸', isAvailable: false, description: 'Coming soon' },
  { id: 'europe', name: 'europe', displayName: 'Europe', location: 'Frankfurt', flag: '🇪🇺', isAvailable: false, description: 'Coming soon' },
  { id: 'asia-pacific', name: 'asia-pacific', displayName: 'Asia Pacific', location: 'Singapore', flag: '🇸🇬', isAvailable: false, description: 'Coming soon' },
];

const regionsForSelection = SHOW_VM_COMING_SOON
  ? regions
  : regions.filter((r) => r.isAvailable);

// Use case options
const useCaseOptions: { value: VMUseCase; label: string; description: string; features: string[] }[] = [
  {
    value: 'development_testing',
    label: 'Development & Testing',
    description: 'Ideal for development, testing, and non-critical workloads',
    features: ['Spot VM option available (50% discount)', 'Best for temporary workloads', 'May be interrupted during high demand']
  },
  {
    value: 'production',
    label: 'Production',
    description: 'For production workloads requiring high availability',
    features: ['Guaranteed uptime', 'No interruptions', 'Recommended for critical applications']
  },
];

const CreateVM: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  
  const [step, setStep] = useState<Step>(projectIdFromUrl ? 'region' : 'project');
  const [selectedGPU, setSelectedGPU] = useState<GPUPricing | null>(null);
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');
  const [showCreateSSHKeyModal, setShowCreateSSHKeyModal] = useState(false);
  const [costEstimate, setCostEstimate] = useState<VMCostEstimate | null>(null);
  const [isEstimatingCost, setIsEstimatingCost] = useState(false);

  // Wallet data for balance check
  const { availableBalance } = useWallet();

  // GPU search / pagination (only when VM_SHOW_GPU_CONFIGURATION)
  const [gpuSearchQuery, setGpuSearchQuery] = useState('');
  const [gpuCurrentPage, setGpuCurrentPage] = useState(1);
  const gpusPerPage = 10;

  const [formData, setFormData] = useState<VMCreateRequest>({
    name: '',
    description: '',
    cpu_cores: 2,
    memory_gb: 4,
    storage_gb: 50,
    gpu_count: 0,
    gpu_type: '',
    os_image: 'ubuntu:22.04',
    ssh_key_id: undefined,
    project_id: projectIdFromUrl || undefined,
    billing_period: 'month',
    region: 'india',
    use_case: 'development_testing',
    is_spot: false,
  });

  // Local state for input values to allow empty during typing
  const [cpuInputValue, setCpuInputValue] = useState<string>('2');
  const [memoryInputValue, setMemoryInputValue] = useState<string>('4');
  const [storageInputValue, setStorageInputValue] = useState<string>('50');
  const [gpuCountInputValue, setGpuCountInputValue] = useState<string>('0');

  const quickTemplates = [
    { name: 'Small', cpu: 1, memory: 2, storage: 20, description: 'Good for small projects, testing and light workloads' },
    { name: 'Medium', cpu: 2, memory: 4, storage: 50, description: 'Ideal for medium projects, web apps and moderate workloads' },
    { name: 'Large', cpu: 4, memory: 8, storage: 100, description: 'Perfect for large projects, databases and heavy workloads' },
    { name: 'XLarge', cpu: 8, memory: 16, storage: 200, description: 'Best for enterprise applications and high-performance workloads' },
  ];

  // Psychology-driven compute classes with sane defaults and better decision framing
  const computeClasses = [
    { id: 'balanced', name: 'Balanced', cpu: 2, memory: 4, storage: 50, description: 'Best default for most web apps and general workloads' },
    { id: 'cpu_intensive', name: 'CPU Intensive', cpu: 4, memory: 4, storage: 50, description: 'Best for build pipelines, compilers, and compute-heavy jobs' },
    { id: 'memory_intensive', name: 'Memory Intensive', cpu: 2, memory: 8, storage: 50, description: 'Best for caches, analytics, and memory-heavy services' },
  ] as const;
  const classConfigurations = {
    balanced: [
      { label: 'Balanced - Starter (2 vCPU, 4 GB)', cpu: 2, memory: 4 },
      { label: 'Balanced - Growth (4 vCPU, 8 GB)', cpu: 4, memory: 8 },
      { label: 'Balanced - Pro (8 vCPU, 16 GB)', cpu: 8, memory: 16 },
    ],
    cpu_intensive: [
      { label: 'CPU - Build (4 vCPU, 4 GB)', cpu: 4, memory: 4 },
      { label: 'CPU - Compute (8 vCPU, 8 GB)', cpu: 8, memory: 8 },
      { label: 'CPU - High Compute (16 vCPU, 16 GB)', cpu: 16, memory: 16 },
    ],
    memory_intensive: [
      { label: 'Memory - Cache (2 vCPU, 8 GB)', cpu: 2, memory: 8 },
      { label: 'Memory - Analytics (4 vCPU, 16 GB)', cpu: 4, memory: 16 },
      { label: 'Memory - Data Heavy (8 vCPU, 32 GB)', cpu: 8, memory: 32 },
    ],
  } as const;
  type ClassConfigOption = { label: string; cpu: number; memory: number };
  const [selectedClassConfiguration, setSelectedClassConfiguration] = useState<string>('');
  const [selectedComputeClass, setSelectedComputeClass] = useState<string>('');
  const [selectedQuickTemplate, setSelectedQuickTemplate] = useState<string>('');
  const [isComputeClassesExpanded, setIsComputeClassesExpanded] = useState<boolean>(false);
  const ENABLE_MANUAL_RESOURCE_OVERRIDE = false;
  const [sshPassword, setSshPassword] = useState<string>('');
  const [confirmSshPassword, setConfirmSshPassword] = useState<string>('');

  // Use the global selected project hook
  const {
    selectedProjectId,
    projects,
    setSelectedProject,
    autoSelectFirstProject,
  } = useSelectedProject();

  const { data: gpuPricingData, isLoading: loadingGPU } = useQuery({
    queryKey: ['gpu-pricing'],
    queryFn: () => pricingService.getGPUPricing().then((res) => res.data),
    enabled: VM_SHOW_GPU_CONFIGURATION,
  });

  const { data: gpuAvailabilityData } = useQuery({
    queryKey: ['gpu-availability'],
    queryFn: () => pricingService.getGPUAvailability().then((res) => res.data),
    enabled: VM_SHOW_GPU_CONFIGURATION,
  });

  const { data: osImagesData, isLoading: loadingOSImages } = useQuery({
    queryKey: ['os-images'],
    queryFn: () => vmApi.listOSImages(undefined, true),
    staleTime: 300000,
  });

  const { data: sshKeysData, refetch: refetchSSHKeys } = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => sshKeyService.list().then((res) => res.data),
    enabled: step === 'ssh' || step === 'review',
  });

  const sshKeys: SSHKey[] = Array.isArray(sshKeysData)
    ? sshKeysData
    : sshKeysData?.data || sshKeysData?.keys || [];

  const osImages: OSImage[] = useMemo(() => osImagesData?.images || [], [osImagesData?.images]);
  const osImagesByCategory = useMemo(() => {
    const list = SHOW_VM_COMING_SOON
      ? osImages
      : osImages.filter((img) => img.is_available);
    return list.reduce((acc, img) => {
      const cat = img.category;
      if (!acc[cat]) {
        acc[cat] = [];
      }
      acc[cat].push(img);
      return acc;
    }, {} as Record<string, OSImage[]>);
  }, [osImages]);

  // Helper to check GPU availability by type
  const getGPUAvailability = (gpuType: string): GPUAvailability | undefined => {
    const availabilityList = Array.isArray(gpuAvailabilityData) ? gpuAvailabilityData : gpuAvailabilityData?.data || [];
    return availabilityList.find((a: GPUAvailability) => a.gpu_type === gpuType);
  };

  // L40S GPU is disabled for now - CPU-only or other GPUs only
  const isL40sDisabled = (gpuType: string) => /l40s|l40\b/i.test(gpuType);

  // Sync input values when formData changes externally (e.g., from templates)
  useEffect(() => {
    setCpuInputValue(formData.cpu_cores.toString());
    setMemoryInputValue(formData.memory_gb.toString());
    setStorageInputValue(formData.storage_gb.toString());
    setGpuCountInputValue((formData.gpu_count || 0).toString());
  }, [formData.cpu_cores, formData.memory_gb, formData.storage_gb, formData.gpu_count]);

  // Auto-select first project if none selected globally
  useEffect(() => {
    if (!projectIdFromUrl) {
      autoSelectFirstProject();
    }
  }, [autoSelectFirstProject, projectIdFromUrl]);

  // Sync formData.project_id with global selectedProjectId (if not from URL)
  useEffect(() => {
    if (!projectIdFromUrl && selectedProjectId && formData.project_id !== selectedProjectId) {
      setFormData(prev => ({ ...prev, project_id: selectedProjectId }));
    }
  }, [selectedProjectId, formData.project_id, projectIdFromUrl]);

  // Estimate cost when form data changes
  useEffect(() => {
    const estimateCost = async () => {
      if (!formData.project_id || formData.cpu_cores < 1 || formData.memory_gb < 0.5 || formData.storage_gb < 10) {
        setCostEstimate(null);
        return;
      }

      setIsEstimatingCost(true);
      try {
        const estimate = await vmApi.estimateVMCost({
          cpu_cores: formData.cpu_cores,
          memory_gb: formData.memory_gb,
          storage_gb: formData.storage_gb,
          gpu_count: formData.gpu_count || 0,
          gpu_type: formData.gpu_type || undefined,
          billing_period: formData.billing_period || 'month',
          use_case: formData.use_case,
          is_spot: formData.is_spot,
        });
        setCostEstimate(estimate);
      } catch (error) {
        console.error('Failed to estimate cost:', error);
        // Don't show error toast, just silently fail
      } finally {
        setIsEstimatingCost(false);
      }
    };

    // Debounce cost estimation
    const timeoutId = setTimeout(estimateCost, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.cpu_cores, formData.memory_gb, formData.storage_gb, formData.gpu_count, formData.gpu_type, formData.billing_period, formData.project_id, formData.use_case, formData.is_spot]);

  const createMutation = useMutation({
    mutationFn: async (data: VMCreateRequest) => {
      if (!data.project_id) {
        throw new Error('Project ID is required');
      }
      return vmApi.createVM(data, data.project_id);
    },
    onSuccess: () => {
      toast.success('VM creation initiated');
      navigate('/vms');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to create VM';
      toast.error(errorMessage);
    },
  });

  const handleSelectGPU = (gpu: GPUPricing | null) => {
    setSelectedGPU(gpu);
    if (gpu) {
      setFormData({
        ...formData,
        gpu_count: 1,
        gpu_type: gpu.gpu_type,
      });
      setGpuCountInputValue('1');
    } else {
      setFormData({
        ...formData,
        gpu_count: 0,
        gpu_type: '',
      });
      setGpuCountInputValue('0');
    }
  };

  const allGpus = Array.isArray(gpuPricingData) ? gpuPricingData : gpuPricingData?.data || [];
  const filteredGpus = allGpus.filter((gpu: GPUPricing) => {
    if (!getGPUAvailability(gpu.gpu_type)?.available_gpus) {
      return false;
    }
    if (gpuSearchQuery) {
      return gpu.gpu_type.toLowerCase().includes(gpuSearchQuery.toLowerCase());
    }
    return true;
  });

  const totalPages = Math.ceil(filteredGpus.length / gpusPerPage);
  const startIndex = (gpuCurrentPage - 1) * gpusPerPage;
  const endIndex = startIndex + gpusPerPage;
  const paginatedGpus = filteredGpus.slice(startIndex, endIndex);

  const steps: Step[] = projectIdFromUrl
    ? ['region', 'useCase', 'basic', 'resources', 'os', 'ssh', 'review']
    : ['project', 'region', 'useCase', 'basic', 'resources', 'os', 'ssh', 'review'];

  const currentStepIndex = steps.indexOf(step);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setStep(steps[currentStepIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setStep(steps[currentStepIndex - 1]);
    }
  };

  const validateVMName = (name: string): string | undefined => {
    if (!name) {
      return 'VM name is required';
    }
    if (name.length > 253) {
      return 'Name must be 253 characters or less';
    }
    const pattern = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/;
    if (!pattern.test(name)) {
      return 'Name must consist of lowercase letters (a-z), numbers (0-9), and hyphens (-). It must start and end with a letter or number.';
    }
    return undefined;
  };

  const [errors, setErrors] = useState<{ name?: string }>({});

  const handleCreate = () => {
    // Validate name
    const nameError = validateVMName(formData.name);
    if (nameError) {
      setErrors({ name: nameError });
      toast.error(nameError);
      return;
    }

    // Validate resources
    if (formData.cpu_cores < 1) {
      toast.error('CPU cores must be at least 1');
      return;
    }
    if (formData.memory_gb < 0.5) {
      toast.error('Memory must be at least 0.5 GB');
      return;
    }
    if (formData.storage_gb < 10) {
      toast.error('Storage must be at least 10 GB');
      return;
    }

    if (!formData.ssh_key_id) {
      if (!sshPassword || sshPassword.length < 8) {
        toast.error('SSH password must be at least 8 characters');
        return;
      }
      if (sshPassword !== confirmSshPassword) {
        toast.error('SSH password and confirmation do not match');
        return;
      }
    }

    if (formData.ssh_key_id && sshPassword) {
      setSshPassword('');
      setConfirmSshPassword('');
    }

    // Validate GPU configuration
    if ((formData.gpu_count || 0) > 0 && !formData.gpu_type) {
      toast.error('Please select a GPU type when GPU count is greater than 0');
      return;
    }

    // Validate OS image
    const selectedOS = osImages.find((img) => img.name === formData.os_image);
    if (!selectedOS || !selectedOS.is_available) {
      toast.error('Please select an available OS image');
      return;
    }

    // Check wallet balance
    if (costEstimate && costEstimate.period_cost > availableBalance) {
      setBalanceErrorMessage(
        `Insufficient balance. Required: ₹${costEstimate.period_cost.toFixed(2)}, Available: ₹${availableBalance.toFixed(2)}`
      );
      setShowInsufficientBalance(true);
      return;
    }

    const selectedSSHKey = formData.ssh_key_id
      ? sshKeys.find((key) => key.id === formData.ssh_key_id)
      : null;

    createMutation.mutate({
      ...formData,
      ssh_public_key: selectedSSHKey?.public_key,
      ssh_password: !formData.ssh_key_id ? sshPassword : undefined,
    });
  };

  const getPeriodLabel = (period: VMBillingPeriod): string => {
    switch (period) {
      case 'month': return 'mo';
      case 'quarter': return 'qtr';
      case 'annual': return 'yr';
      default: return period;
    }
  };

  return (
    <div>
      <Header
        title="Create Virtual Machine"
        subtitle="Launch a new VM instance"
        actions={
          <button
            onClick={() => navigate('/vms')}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to VMs
          </button>
        }
      />

      <div className="p-6 max-w-7xl mx-auto">
        {/* Progress Steps */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-8">
          <div className="flex items-center justify-between">
            {steps.map((s, i) => {
              const isCompleted = currentStepIndex > i;
              const isCurrent = step === s;
              const isClickable = i <= currentStepIndex;

              const stepLabel = s === 'project' ? 'Project' :
                s === 'region' ? 'Region' :
                s === 'useCase' ? 'Use Case' :
                s === 'basic' ? 'Basic Info' :
                s === 'resources' ? 'Resources' :
                s === 'os' ? 'OS Image' :
                s === 'ssh' ? 'SSH Config' :
                'Review';

              return (
                <React.Fragment key={s}>
                  <button
                    onClick={() => isClickable && setStep(s)}
                    disabled={!isClickable}
                    className={`flex flex-col items-center gap-2 group ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    {/* Step Circle */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-primary-500 text-white ring-4 ring-primary-500/30'
                            : 'bg-gray-700 text-gray-400 group-hover:bg-gray-600'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon className="w-6 h-6" />
                      ) : (
                        <span>{i + 1}</span>
                      )}
                    </div>
                    {/* Step Label */}
                    <span
                      className={`text-xs font-medium transition-colors hidden sm:block ${
                        isCompleted
                          ? 'text-green-400'
                          : isCurrent
                            ? 'text-primary-400'
                            : 'text-gray-500'
                      }`}
                    >
                      {stepLabel}
                    </span>
                  </button>
                  {/* Connector Line */}
                  {i < steps.length - 1 && (
                    <div className="flex-1 mx-2 sm:mx-4">
                      <div
                        className={`h-1 rounded-full transition-colors ${
                          currentStepIndex > i ? 'bg-green-500' : 'bg-gray-700'
                        }`}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {/* Current Step Title - Mobile */}
          <div className="mt-4 text-center sm:hidden">
            <span className="text-sm font-medium text-primary-400">
              Step {currentStepIndex + 1}: {
                step === 'project' ? 'Project' :
                step === 'region' ? 'Region' :
                step === 'useCase' ? 'Use Case' :
                step === 'basic' ? 'Basic Info' :
                step === 'resources' ? 'Resources' :
                step === 'os' ? 'OS Image' :
                step === 'ssh' ? 'SSH Config' :
                'Review'
              }
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">
            {/* Step 1: Project Selection */}
            {step === 'project' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Select Project
                </h2>
                <p className="text-gray-400 mb-6">
                  Choose the project where this VM will be created
                </p>
                <div className="space-y-3">
                  {projects.map((project: any) => (
                    <button
                      key={project.id}
                      onClick={() => {
                        setFormData({ ...formData, project_id: project.id });
                        // Also update the global project selection
                        setSelectedProject(project.id);
                        handleNext();
                      }}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                        formData.project_id === project.id
                          ? 'border-primary-500 bg-primary-500/20'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <h3 className="font-semibold text-white">{project.name}</h3>
                      {project.description && (
                        <p className="text-sm text-gray-400 mt-1">{project.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Region Selection */}
            {step === 'region' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Select Region
                </h2>
                <p className="text-gray-400 mb-6">
                  Choose the region where your VM will be deployed
                </p>
                <div className="space-y-3">
                  {regionsForSelection.map((region) => (
                    <button
                      key={region.id}
                      onClick={() => {
                        if (region.isAvailable) {
                          setFormData({ ...formData, region: region.id });
                          handleNext();
                        }
                      }}
                      disabled={!region.isAvailable}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                        formData.region === region.id
                          ? 'border-primary-500 bg-primary-500/20'
                          : region.isAvailable
                            ? 'border-gray-700 hover:border-gray-600'
                            : 'border-gray-700 bg-gray-800 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{region.flag}</span>
                          <div>
                            <h3 className="font-semibold text-white">{region.displayName}</h3>
                            <p className="text-sm text-gray-400">{region.location}</p>
                          </div>
                        </div>
                        {!region.isAvailable && (
                          <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">Coming Soon</span>
                        )}
                        {region.isAvailable && formData.region === region.id && (
                          <CheckCircleIcon className="w-6 h-6 text-primary-500" />
                        )}
                      </div>
                      {region.description && (
                        <p className="text-sm text-gray-500 mt-2 ml-11">{region.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Use Case Selection */}
            {step === 'useCase' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Select Use Case
                </h2>
                <p className="text-gray-400 mb-6">
                  Choose the type of workload for your VM
                </p>
                <div className="space-y-4">
                  {useCaseOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setFormData({
                          ...formData,
                          use_case: option.value,
                          // Reset is_spot when switching to production
                          is_spot: option.value === 'production' ? false : formData.is_spot,
                        });
                      }}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                        formData.use_case === option.value
                          ? 'border-primary-500 bg-primary-500/20'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-white text-lg">{option.label}</h3>
                        {formData.use_case === option.value && (
                          <CheckCircleIcon className="w-6 h-6 text-primary-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-400 mb-3">{option.description}</p>
                      <ul className="space-y-1">
                        {option.features.map((feature, idx) => (
                          <li key={idx} className="text-xs text-gray-500 flex items-center gap-2">
                            <span className="w-1 h-1 bg-gray-500 rounded-full"></span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>

                {/* Spot VM option - only for development_testing */}
                {formData.use_case === 'development_testing' && (
                  <div className="mt-6 p-4 border-2 border-dashed border-gray-700 rounded-lg">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_spot || false}
                        onChange={(e) => setFormData({ ...formData, is_spot: e.target.checked })}
                        className="w-5 h-5 mt-0.5 text-primary-500 focus:ring-primary-500 border-gray-600 rounded"
                      />
                      <div>
                        <h4 className="font-semibold text-white">Use Spot VM</h4>
                        <p className="text-sm text-gray-400 mt-1">
                          Get <span className="text-green-400 font-semibold">50% discount</span> on compute costs. Spot VMs may be interrupted when capacity is needed.
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Basic Info */}
            {step === 'basic' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Basic Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      VM Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase();
                        setFormData({ ...formData, name: value });
                        const error = validateVMName(value);
                        setErrors({ ...errors, name: error });
                      }}
                      className={`input w-full ${errors.name ? 'border-red-300' : ''}`}
                      placeholder="my-vm"
                      required
                    />
                    {errors.name ? (
                      <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-400">
                        Use lowercase letters, numbers, and hyphens only
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="input w-full"
                      rows={3}
                      placeholder="Optional description"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Resources */}
            {step === 'resources' && (
              <div className="space-y-6">
                {/* Quick Templates */}
                <div className="card">
                  <h2 className="text-xl font-semibold text-white mb-2">
                    Quick Templates
                  </h2>
                  <p className="text-sm text-gray-400 mb-4">
                    Fast presets for CPU, Memory, and Storage. Choosing a template will clear any selected compute class.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {quickTemplates.map((template) => (
                      <button
                        key={template.name}
                        type="button"
                        onClick={() => {
                          setSelectedQuickTemplate(template.name);
                          setSelectedComputeClass('');
                          setSelectedClassConfiguration('');
                          setFormData({
                            ...formData,
                            cpu_cores: template.cpu,
                            memory_gb: template.memory,
                            storage_gb: template.storage,
                          });
                          setCpuInputValue(template.cpu.toString());
                          setMemoryInputValue(template.memory.toString());
                          setStorageInputValue(template.storage.toString());
                        }}
                        className={`p-4 border-2 rounded-lg transition-colors text-left ${
                          selectedQuickTemplate === template.name
                            ? 'border-primary-500 bg-primary-500/20'
                            : 'border-gray-700 hover:border-primary-300 hover:bg-primary-500/20'
                        }`}
                      >
                        <div className="font-semibold text-white mb-1">{template.name}</div>
                        <div className="text-xs text-gray-400 space-y-0.5">
                          <div>{template.cpu} vCPU</div>
                          <div>{template.memory} GB RAM</div>
                          <div>{template.storage} GB Storage</div>
                        </div>
                        <div className="text-xs text-gray-400 mt-2">{template.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Compute Classes */}
                <div className="card">
                  <button
                    type="button"
                    onClick={() => setIsComputeClassesExpanded((prev) => !prev)}
                    className="w-full flex items-center justify-between"
                  >
                    <h2 className="text-xl font-semibold text-white">
                      Compute Classes
                    </h2>
                    {isComputeClassesExpanded ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  <p className="text-sm text-gray-400 mt-2 mb-4">
                    Workload-focused presets. Choosing a compute class will clear any selected quick template.
                  </p>
                  <div className={`${isComputeClassesExpanded ? 'block' : 'hidden'}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {computeClasses.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => {
                            setSelectedComputeClass(template.id);
                            setSelectedQuickTemplate('');
                            setSelectedClassConfiguration('');
                            setFormData({
                              ...formData,
                              cpu_cores: template.cpu,
                              memory_gb: template.memory,
                              storage_gb: template.storage,
                            });
                            setCpuInputValue(template.cpu.toString());
                            setMemoryInputValue(template.memory.toString());
                            setStorageInputValue(template.storage.toString());
                          }}
                          className={`p-4 border-2 rounded-lg transition-colors text-left ${
                            selectedComputeClass === template.id
                              ? 'border-primary-500 bg-primary-500/20'
                              : 'border-gray-700 hover:border-primary-300 hover:bg-primary-500/20'
                          }`}
                        >
                          <div className="font-semibold text-white mb-1">{template.name}</div>
                          <div className="text-xs text-gray-400 mt-2">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h2 className="text-xl font-semibold text-white mb-4">
                    Compute Resources
                  </h2>
                  <p className="text-xs text-gray-400 mb-3">
                    CPU and Memory are locked to class configuration. Toggle `ENABLE_MANUAL_RESOURCE_OVERRIDE` to re-enable manual edits.
                  </p>
                  <div className="space-y-4">
                    {selectedComputeClass && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          VM Configuration (Class Presets)
                        </label>
                        <select
                          value={selectedClassConfiguration}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedClassConfiguration(value);
                            const options: ReadonlyArray<ClassConfigOption> =
                              classConfigurations[selectedComputeClass as keyof typeof classConfigurations] || [];
                            const selected = options.find((opt: ClassConfigOption) => opt.label === value);
                            if (selected) {
                              setFormData({ ...formData, cpu_cores: selected.cpu, memory_gb: selected.memory });
                              setCpuInputValue(selected.cpu.toString());
                              setMemoryInputValue(selected.memory.toString());
                            }
                          }}
                          className="input w-full"
                        >
                          <option value="">Select a pre-defined VM configuration</option>
                          {(classConfigurations[selectedComputeClass as keyof typeof classConfigurations] || []).map((opt) => (
                            <option key={opt.label} value={opt.label}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        CPU Cores *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="32"
                        value={cpuInputValue}
                        onChange={(e) => {
                          if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                          const value = e.target.value;
                          setCpuInputValue(value);
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 1) {
                            setSelectedComputeClass('custom');
                            setFormData({ ...formData, cpu_cores: numValue });
                          }
                        }}
                        onBlur={(e) => {
                          if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                          const numValue = parseInt(e.target.value);
                          if (isNaN(numValue) || numValue < 1) {
                            setCpuInputValue('1');
                            setFormData({ ...formData, cpu_cores: 1 });
                          }
                        }}
                        disabled={!ENABLE_MANUAL_RESOURCE_OVERRIDE}
                        className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-60 disabled:cursor-not-allowed ${
                          formData.cpu_cores < 1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                        required
                      />
                      {formData.cpu_cores < 1 && (
                        <p className="mt-1 text-xs text-red-600 font-medium">
                          Minimum: 1 CPU core required
                        </p>
                      )}
                      {formData.cpu_cores >= 1 && (
                        <p className="mt-1 text-xs text-gray-400">
                          Minimum: 1 CPU core
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Memory (GB) *
                      </label>
                      <input
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={memoryInputValue}
                        onChange={(e) => {
                          if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                          const value = e.target.value;
                          setMemoryInputValue(value);
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue) && numValue >= 0.5) {
                            setSelectedComputeClass('custom');
                            setFormData({ ...formData, memory_gb: numValue });
                          }
                        }}
                        onBlur={(e) => {
                          if (!ENABLE_MANUAL_RESOURCE_OVERRIDE) return;
                          const numValue = parseFloat(e.target.value);
                          if (isNaN(numValue) || numValue < 0.5) {
                            setMemoryInputValue('0.5');
                            setFormData({ ...formData, memory_gb: 0.5 });
                          }
                        }}
                        disabled={!ENABLE_MANUAL_RESOURCE_OVERRIDE}
                        className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-60 disabled:cursor-not-allowed ${
                          formData.memory_gb < 0.5 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                        required
                      />
                      {formData.memory_gb < 0.5 && (
                        <p className="mt-1 text-xs text-red-600 font-medium">
                          Minimum: 0.5 GB required
                        </p>
                      )}
                      {formData.memory_gb >= 0.5 && (
                        <p className="mt-1 text-xs text-gray-400">
                          Minimum: 0.5 GB
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Storage (GB) *
                      </label>
                      <input
                        type="number"
                        min="10"
                        value={storageInputValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          setStorageInputValue(value);
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue) && numValue >= 10) {
                            setFormData({ ...formData, storage_gb: numValue });
                          }
                        }}
                        onBlur={(e) => {
                          const numValue = parseFloat(e.target.value);
                          if (isNaN(numValue) || numValue < 10) {
                            setStorageInputValue('10');
                            setFormData({ ...formData, storage_gb: 10 });
                          }
                        }}
                        className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                          formData.storage_gb < 10 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                        required
                      />
                      {formData.storage_gb < 10 && (
                        <p className="mt-1 text-xs text-red-600 font-medium">
                          Minimum: 10 GB required
                        </p>
                      )}
                      {formData.storage_gb >= 10 && (
                        <p className="mt-1 text-xs text-gray-400">
                          Minimum: 10 GB (boot disk baseline)
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {VM_SHOW_GPU_CONFIGURATION && (
                <div className="card">
                  <h2 className="text-xl font-semibold text-white mb-4">
                    GPU Configuration (Optional)
                  </h2>
                  <p className="text-gray-400 mb-6">
                    Only GPUs with available capacity are listed. Choose one or stay on CPU only.
                  </p>

                  {loadingGPU ? (
                    <div className="flex justify-center py-12">
                      <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Search */}
                      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Search GPUs (e.g., A100, RTX, H100)..."
                              value={gpuSearchQuery}
                              onChange={(e) => {
                                setGpuSearchQuery(e.target.value);
                                setGpuCurrentPage(1);
                              }}
                              className="w-full px-4 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-900 text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* No GPU Option */}
                      <button
                        onClick={() => handleSelectGPU(null)}
                        className={`w-full p-4 border-2 rounded-xl hover:border-primary-300 hover:bg-primary-500/20 transition-colors text-left ${
                          !selectedGPU ? 'border-primary-500 bg-primary-500/20' : 'border-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-gray-700 rounded-xl">
                            <CpuChipIcon className="w-8 h-8 text-gray-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white">
                              CPU Only Instance
                            </h3>
                            <p className="text-sm text-gray-400">
                              No GPU - ideal for general purpose workloads
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* GPU Options */}
                      {filteredGpus.length === 0 ? (
                        <div className="text-center py-12">
                          <CpuChipIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">No GPUs found matching your criteria</p>
                        </div>
                      ) : (
                        <>
                          {paginatedGpus.map((gpu: GPUPricing) => {
                            const availability = getGPUAvailability(gpu.gpu_type);
                            const availableCount = parseFloat((availability?.available_gpus ?? 0).toFixed(2));
                            const totalCount = availability?.total_gpus ?? 0;
                            const isAvailable = availability !== undefined && availableCount > 0;
                            const isSelected = selectedGPU?.gpu_type === gpu.gpu_type;
                            const isL40s = isL40sDisabled(gpu.gpu_type);
                            const clickable = isAvailable && !isL40s;

                            return (
                              <div
                                key={gpu.gpu_type}
                                className={`w-full p-4 border-2 rounded-xl text-left ${
                                  isSelected
                                    ? 'border-primary-500 bg-primary-500/20'
                                    : clickable
                                      ? 'border-gray-700 hover:border-primary-300 hover:bg-primary-500/20 cursor-pointer'
                                      : 'border-gray-700 bg-gray-800 cursor-not-allowed opacity-70'
                                } transition-colors`}
                                onClick={() => clickable && handleSelectGPU(gpu)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-xl ${
                                      clickable ? 'bg-green-100' : 'bg-gray-200'
                                    }`}>
                                      <CpuChipIcon className={`w-8 h-8 ${
                                        clickable ? 'text-green-600' : 'text-gray-500'
                                      }`} />
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className={`font-semibold ${clickable ? 'text-white' : 'text-gray-500'}`}>
                                          {gpu.gpu_type}
                                        </h3>
                                        {isL40s ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-600 text-gray-300">
                                            Unavailable
                                          </span>
                                        ) : isAvailable ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                            {availableCount}/{totalCount} Available
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                            0/{totalCount} Available
                                          </span>
                                        )}
                                      </div>
                                      <p className={`text-sm ${clickable ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {gpu.memory_gb}GB VRAM
                                        {gpu.cuda_cores > 0 && ` • ${gpu.cuda_cores.toLocaleString()} CUDA Cores`}
                                        {gpu.tensor_cores > 0 && ` • ${gpu.tensor_cores} Tensor Cores`}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className={`text-lg font-semibold ${clickable ? 'text-white' : 'text-gray-500'}`}>
                                      ₹{gpu.price_per_minute.toFixed(2)}/min
                                    </p>
                                    <p className="text-sm text-gray-400">
                                      ₹{(gpu.price_per_minute * 60).toFixed(0)}/hr
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Pagination */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-700 pt-4 mt-4">
                              <div className="text-sm text-gray-400">
                                Showing {startIndex + 1}-{Math.min(endIndex, filteredGpus.length)} of {filteredGpus.length} GPUs
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setGpuCurrentPage(Math.max(1, gpuCurrentPage - 1))}
                                  disabled={gpuCurrentPage === 1}
                                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                    gpuCurrentPage === 1
                                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                      : 'bg-gray-900 border border-gray-600 text-gray-300 hover:bg-gray-800'
                                  }`}
                                >
                                  Previous
                                </button>
                                <div className="flex gap-1">
                                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                      key={page}
                                      onClick={() => setGpuCurrentPage(page)}
                                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                        page === gpuCurrentPage
                                          ? 'bg-primary-500 text-white'
                                          : 'bg-gray-900 border border-gray-600 text-gray-300 hover:bg-gray-800'
                                      }`}
                                    >
                                      {page}
                                    </button>
                                  ))}
                                </div>
                                <button
                                  onClick={() => setGpuCurrentPage(Math.min(totalPages, gpuCurrentPage + 1))}
                                  disabled={gpuCurrentPage === totalPages}
                                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                    gpuCurrentPage === totalPages
                                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                      : 'bg-gray-900 border border-gray-600 text-gray-300 hover:bg-gray-800'
                                  }`}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* GPU Count Input - shown when GPU is selected */}
                  {selectedGPU && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        GPU Count *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        value={gpuCountInputValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          setGpuCountInputValue(value);
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 1 && numValue <= 8) {
                            setFormData({
                              ...formData,
                              gpu_count: numValue,
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const numValue = parseInt(e.target.value);
                          if (isNaN(numValue) || numValue < 1) {
                            setGpuCountInputValue('1');
                            setFormData({
                              ...formData,
                              gpu_count: 1,
                            });
                          } else if (numValue > 8) {
                            setGpuCountInputValue('8');
                            setFormData({
                              ...formData,
                              gpu_count: 8,
                            });
                          }
                        }}
                        className={`input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                          (formData.gpu_count || 0) < 1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                        }`}
                        required
                      />
                      {(formData.gpu_count || 0) < 1 && (
                        <p className="mt-1 text-xs text-red-600 font-medium">
                          Minimum: 1 GPU required
                        </p>
                      )}
                      {(formData.gpu_count || 0) >= 1 && (
                        <p className="mt-1 text-xs text-gray-400">
                          Number of {selectedGPU.gpu_type} GPUs (1-8)
                        </p>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>
            )}

            {/* Step 4: OS Image Selection */}
            {step === 'os' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Operating System Image
                </h2>
                <p className="text-gray-400 mb-6">
                  Choose an OS image for your VM
                </p>
                {loadingOSImages ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(osImagesByCategory).map(([category, images]) => (
                      <div key={category}>
                        <h3 className="text-sm font-medium text-gray-300 mb-2 capitalize">
                          {category === 'general' ? 'General Purpose' :
                           category === 'ml' ? 'Machine Learning / AI' :
                           category === 'automation' ? 'Automation' :
                           'Custom'}
                        </h3>
                        <div className="space-y-2">
                          {images.map((img) => (
                            <button
                              key={img.id}
                              onClick={() => setFormData({ ...formData, os_image: img.name })}
                              disabled={!img.is_available}
                              className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                                formData.os_image === img.name
                                  ? 'border-primary-500 bg-primary-500/20'
                                  : img.is_available
                                    ? 'border-gray-700 hover:border-gray-600'
                                    : 'border-gray-700 bg-gray-800 opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-semibold text-white">{img.display_name}</h4>
                                  {img.description && (
                                    <p className="text-sm text-gray-400 mt-1">{img.description}</p>
                                  )}
                                </div>
                                {!img.is_available && (
                                  <span className="text-xs text-gray-400">Coming Soon</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: SSH Configuration */}
            {step === 'ssh' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-white mb-4">
                  SSH Configuration
                </h2>
                <p className="text-gray-400 mb-6">
                  Select an SSH key for secure access, or choose password authentication now.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      SSH Key (Optional)
                    </label>
                    <select
                      value={formData.ssh_key_id || ''}
                      onChange={(e) => setFormData({ ...formData, ssh_key_id: e.target.value || undefined })}
                      className="input w-full"
                    >
                      <option value="">None (Use password authentication)</option>
                      {sshKeys.map((key) => (
                        <option key={key.id} value={key.id}>
                          {key.name} {key.is_default ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-sm text-gray-400">
                      {formData.ssh_key_id
                        ? 'Selected SSH key will be used for authentication'
                        : 'No SSH key selected. Set an SSH password now and connect immediately after VM is ready.'}
                    </p>
                    {sshKeys.length === 0 && (
                      <button
                        onClick={() => setShowCreateSSHKeyModal(true)}
                        className="mt-2 text-sm text-primary-500 hover:text-primary-400 font-medium"
                      >
                        Create SSH Key
                      </button>
                    )}
                  </div>
                  {!formData.ssh_key_id && (
                    <div className="space-y-3 p-4 rounded-lg border border-gray-700 bg-gray-800/40">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          SSH Password *
                        </label>
                        <input
                          type="password"
                          value={sshPassword}
                          onChange={(e) => setSshPassword(e.target.value)}
                          className="input w-full"
                          placeholder="Enter password (minimum 8 characters)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Confirm SSH Password *
                        </label>
                        <input
                          type="password"
                          value={confirmSshPassword}
                          onChange={(e) => setConfirmSshPassword(e.target.value)}
                          className="input w-full"
                          placeholder="Confirm password"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 8: Review */}
            {step === 'review' && (
              <div className="card">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Review Configuration
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-400">VM Name</p>
                      <p className="font-medium text-white">{formData.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Project</p>
                      <p className="font-medium text-white">
                        {projects.find((p: any) => p.id === formData.project_id)?.name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Region</p>
                      <p className="font-medium text-white">
                        {regions.find((r) => r.id === formData.region)?.flag}{' '}
                        {regions.find((r) => r.id === formData.region)?.displayName || formData.region}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Use Case</p>
                      <p className="font-medium text-white">
                        {useCaseOptions.find((u) => u.value === formData.use_case)?.label || formData.use_case}
                        {formData.is_spot && <span className="ml-2 text-xs text-green-400">(Spot VM)</span>}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">CPU Cores</p>
                      <p className="font-medium text-white">{formData.cpu_cores}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Memory</p>
                      <p className="font-medium text-white">{formData.memory_gb} GB</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Storage</p>
                      <p className="font-medium text-white">{formData.storage_gb} GB</p>
                    </div>
                    {VM_SHOW_GPU_CONFIGURATION && (
                    <div>
                      <p className="text-sm text-gray-400">GPU</p>
                      <p className="font-medium text-white">
                        {(formData.gpu_count || 0) > 0
                          ? `${formData.gpu_count}x ${formData.gpu_type}`
                          : 'None'}
                      </p>
                    </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-400">OS Image</p>
                      <p className="font-medium text-white">
                        {osImages.find((img) => img.name === formData.os_image)?.display_name || formData.os_image}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Billing Period</p>
                      <p className="font-medium text-white capitalize">{formData.billing_period}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6">
              <button
                onClick={handlePrevious}
                disabled={currentStepIndex === 0}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {currentStepIndex < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (step === 'project' && !formData.project_id) ||
                    (step === 'region' && !formData.region) ||
                    (step === 'useCase' && !formData.use_case) ||
                    (step === 'basic' && (!formData.name || !!errors.name)) ||
                    (step === 'resources' && (formData.cpu_cores < 1 || formData.memory_gb < 0.5 || formData.storage_gb < 10 || ((formData.gpu_count || 0) > 0 && !formData.gpu_type))) ||
                    (step === 'ssh' && !formData.ssh_key_id && (!sshPassword || sshPassword.length < 8 || confirmSshPassword !== sshPassword)) ||
                    (step === 'os' && !formData.os_image)
                  }
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={
                    !formData.name || 
                    !formData.project_id || 
                    createMutation.isPending ||
                    !!errors.name ||
                    formData.storage_gb < 10 ||
                    ((formData.gpu_count || 0) > 0 && !formData.gpu_type) ||
                    (!formData.ssh_key_id && (!sshPassword || sshPassword.length < 8 || confirmSshPassword !== sshPassword))
                  }
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create VM'}
                </button>
              )}
            </div>
          </div>

          {/* Pricing Sidebar */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="card lg:sticky lg:top-24">
              <h3 className="font-semibold text-white mb-4">Summary</h3>

              <div className="space-y-3 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">Region</span>
                  <span className="font-medium text-white">
                    {regions.find((r) => r.id === formData.region)?.flag}{' '}
                    {regions.find((r) => r.id === formData.region)?.displayName || formData.region}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Use Case</span>
                  <span className="font-medium text-white">
                    {useCaseOptions.find((u) => u.value === formData.use_case)?.label || formData.use_case}
                  </span>
                </div>
                {formData.is_spot && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">VM Type</span>
                    <span className="font-medium text-green-400">Spot (50% off)</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">CPU</span>
                  <span className="font-medium text-white">{formData.cpu_cores} vCPU</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Memory</span>
                  <span className="font-medium text-white">{formData.memory_gb} GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Storage</span>
                  <span className="font-medium text-white">{formData.storage_gb} GB</span>
                </div>
                {(formData.gpu_count || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">GPU</span>
                    <span className="font-medium text-white">
                      {formData.gpu_count}x {formData.gpu_type}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Billing</span>
                  <span className="font-medium text-white capitalize">
                    {formData.billing_period}
                  </span>
                </div>
              </div>

              <hr className="my-4" />

              {/* Billing Period Selector */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">Billing Period</h4>
                <div className="grid grid-cols-2 gap-2">
                  {billingPeriods.map((period) => (
                    <button
                      key={period.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, billing_period: period.value })}
                      className={`p-2 border-2 rounded-lg text-center text-xs font-medium transition-colors ${
                        formData.billing_period === period.value
                          ? 'border-primary-500 bg-primary-500/20 text-primary-500'
                          : 'border-gray-700 hover:border-gray-600 text-gray-300'
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              </div>

              <hr className="my-4" />

              {/* Cost Breakdown */}
              {isEstimatingCost ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin"></div>
                </div>
              ) : costEstimate ? (
                <div className="space-y-2 text-xs">
                  {costEstimate.hourly_cpu_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-blue-500/10">
                      <span className="text-gray-300 font-medium">CPU</span>
                      <span className="text-blue-400 font-semibold">₹{costEstimate.hourly_cpu_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_memory_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-green-500/10">
                      <span className="text-gray-300 font-medium">Memory</span>
                      <span className="text-green-400 font-semibold">₹{costEstimate.hourly_memory_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_storage_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-purple-500/10">
                      <span className="text-gray-300 font-medium">Storage</span>
                      <span className="text-purple-400 font-semibold">₹{costEstimate.hourly_storage_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_gpu_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-orange-500/10">
                      <span className="text-gray-300 font-medium">GPU</span>
                      <span className="text-orange-400 font-semibold">₹{costEstimate.hourly_gpu_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.hourly_gpu_memory_cost > 0 && (
                    <div className="flex justify-between items-center py-1 px-2 rounded bg-teal-500/10">
                      <span className="text-gray-300 font-medium">GPU Memory</span>
                      <span className="text-teal-400 font-semibold">₹{costEstimate.hourly_gpu_memory_cost.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {costEstimate.discount_percent > 0 && (
                    <div className="flex justify-between items-center py-1.5 px-2 rounded bg-green-500/20 mt-2">
                      <span className="text-green-400 font-medium">Discount</span>
                      <span className="text-green-400 font-bold">{costEstimate.discount_percent.toFixed(1)}%</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-lg pt-3 border-t-2 border-gray-700 mt-2">
                    <span className="font-semibold text-white">Est. Cost</span>
                    <span className="font-bold text-primary-500">
                      ₹{costEstimate.period_cost.toFixed(2)}/{getPeriodLabel(costEstimate.billing_period)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 text-right pt-1">
                    ≈ ₹{costEstimate.hourly_total_cost.toFixed(2)}/hr
                  </div>
                  {costEstimate.billing_period !== 'minute' && (
                    <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-800 mt-2">
                      ≈ ₹{(costEstimate.period_cost / costEstimate.period_hours / 60).toFixed(4)}/min
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">
                  Configure resources to see cost estimate
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateSSHKeyModal && (
        <CreateSSHKeyModal
          isOpen={showCreateSSHKeyModal}
          onClose={() => {
            setShowCreateSSHKeyModal(false);
            refetchSSHKeys();
          }}
        />
      )}

      {showInsufficientBalance && (
        <InsufficientBalanceModal
          isOpen={showInsufficientBalance}
          onClose={() => setShowInsufficientBalance(false)}
          message={balanceErrorMessage}
          requiredAmount={costEstimate?.period_cost || 0}
          currentBalance={availableBalance}
        />
      )}
    </div>
  );
};

export default CreateVM;